import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {
    PanelOffset,
    ScrollBarShadows,
    SheetTabOffset,
    SpreadsheetTabsState
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetTabsState';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SpreadsheetEditorState, SpreadsheetPerTabViewState} from '@mintdata-wsp/common/state/ui/visualDesigner/SpreadsheetEditorState';
import {SpreadsheetTabsView} from '@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/tabsPanel/SpreadsheetTabsView';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {action} from 'mobx';

type SpreadsheetTabsListener = {
    onActiveSheetChanged():void;
}

export class SpreadsheetTabsController implements HasEvents<SpreadsheetTabsListener>
{
    private _getState(workbookId:WorkbookId):SpreadsheetTabsState | null
    {
        return this._getEditorState(workbookId).tabs;
    }

    private _getEditorState(workbookId:WorkbookId):SpreadsheetEditorState
    {
        const {main} = commonControllers.spreadsheets;
        const workbook:Workbook = main.findWorkbookById(workbookId);

        if( !workbook )
            return null;

        return workbook.editor;
    }

    public setTabsViewInstance(workbookId:WorkbookId, instance:SpreadsheetTabsView):void
    {
        const state:SpreadsheetTabsState | null = this._getState(workbookId);

        if( state )
            state.tabsViewInstance = instance;
    }

    public getTabsViewInstance(workbookId:WorkbookId):SpreadsheetTabsView | null
    {
        const state:SpreadsheetTabsState | null = this._getState(workbookId);

        if( state )
            return state.tabsViewInstance;
    }

    @action
    public setActiveSheet(workbookId:WorkbookId, sheetId:SheetId, fireEvent:boolean = true):void
    {
        const {main} = commonControllers.spreadsheets;
        const foundSheet:AnySheet = main.findAnySheetById(sheetId);

        if( !foundSheet )
        {
            console.warn(`Cannot set active sheet, sheetId '${sheetId}' not found in workbook`);
            return;
        }

        if( foundSheet.workbook.id != workbookId )
        {
            console.warn(`Sheet ${sheetId} does not belong to workbook ${workbookId}`);
        }

        let spreadsheetTabsState = this._getState(workbookId);

        if( spreadsheetTabsState.activeSheetId === sheetId )
        {
            return;
        }

        spreadsheetTabsState.activeSheetId = sheetId;
        spreadsheetTabsState.editorSheetNameId = null;
        spreadsheetTabsState.editorSheetName = null;
        spreadsheetTabsState.editorErrorsAllowed = false;

        const editorState = this._getEditorState(workbookId);

        if( !(editorState.input.isSelectCellRefMode || editorState.input.isSelectCellRefRangeMode) )
        {
            editorState.isEditMode = false;
            editorState.editSheetId = null;
        }

        editorState.isShowCellRefHint = editorState.isEditMode;

        if( fireEvent )
            setTimeout(() => EventBus.fire(this, 'activeSheetChanged'), 100);
    }

    public isActiveSheet(workbookId:WorkbookId, sheetId:SheetId):boolean
    {
        const activeSheet:AnySheet = this._getState(workbookId).activeSheet;
        return activeSheet && activeSheet.id === sheetId;
    }

    public getActiveSheet(workbookId:WorkbookId):AnySheet
    {
        return this._getState(workbookId).activeSheet;
    }

    public getTabState(workbookId:WorkbookId):SpreadsheetTabsState
    {
        return this._getState(workbookId);
    }

    @action.bound
    public setEditorSheetNameId(workbookId:WorkbookId, sheetId:string):void
    {
        this._getState(workbookId).editorSheetNameId = sheetId;
    }

    @action.bound
    public setEditorSheetName(workbookId:WorkbookId, name:string):void
    {
        this._getState(workbookId).editorSheetName = name;
    }

    @action.bound
    public disableEditorErrors(workbookId:WorkbookId):void
    {
        this._getState(workbookId).editorErrorsAllowed = false;
    }

    @action.bound
    public enableEditorErrors(workbookId:WorkbookId):void
    {
        this._getState(workbookId).editorErrorsAllowed = true;
    }

    @action.bound
    public setIsScrollableTabs(workbookId:WorkbookId, isScrollableTabs:boolean):void
    {
        this._getState(workbookId).isScrollableTabs = isScrollableTabs;
    }

    @action.bound
    public setTabsScrollBarShadows(workbookId:WorkbookId, data:ScrollBarShadows):void
    {
        this._getState(workbookId).scrollBarShadows = data;
    }

    @action.bound
    public setDndTargetSheetIndex(workbookId:WorkbookId, index:number):void
    {
        this._getState(workbookId).dndTargetSheetIndex = index;
    }

    @action.bound
    public setDndSourceSheetIndex(workbookId:WorkbookId, index:number):void
    {
        this._getState(workbookId).dndSourceSheetIndex = index;
    }

    @action.bound
    public setDndSourceSheetTabOffset(workbookId:WorkbookId, offset:SheetTabOffset):void
    {
        this._getState(workbookId).dndSourceSheetTabOffset = offset;
    }

    @action.bound
    public setPanelOffset(workbookId:WorkbookId, offset:PanelOffset):void
    {
        this._getState(workbookId).panelOffset = offset;
    }

    @action.bound
    public setIsInProcess(workbookId:WorkbookId, isInProcess:boolean):void
    {
        this._getState(workbookId).isInProcess = isInProcess;
    }

    @action.bound
    public getOrCreateState(workbookId:WorkbookId, mainSheetId:SheetId):SpreadsheetPerTabViewState
    {
        const {main} = commonControllers.spreadsheets;

        const workbook = main.findWorkbookById(workbookId);
        let perTabState = workbook.editor.perTab.get(mainSheetId);

        if( !perTabState )
        {
            perTabState = new SpreadsheetPerTabViewState();
            workbook.editor.perTab.set(mainSheetId, perTabState);
        }

        return perTabState;
    }
}
